import { Button, ButtonVariant, Icon } from '@lightspeed/design-system-react'
import { T } from '@transifex/react'
import React, { ReactNode, useCallback, useEffect, useState } from 'react'
import { FEATURE_SALE_ADDRESS_MODAL_REFACTOR } from '../../../../../constants/wr-features'
import { FulfilmentDetail } from '../../../../../models/fulfilment'
import { useSaleManager } from '../../../../../ng-compat/hooks'
import { useAppDispatch, useAppSelector } from '../../../../../state/hooks'
import {
  selectSaleFulfilmentDetail,
  setFulfilmentDetails,
} from '../../../../../state/slices/currentSale/currentSaleSlice'
import { getFeatureFromCache } from '../../../../../util/feature-cache'
import { useSaleFulfilment } from '../hook/useSaleFulfilment'
import {
  HEAP_DATA_TRACK_ADD_ADDRESS,
  HEAP_DATA_TRACK_EDIT_ADDRESS,
} from '../../../sale-model.constant'
import { SaleAddressModal } from './SaleAddressModal'

export const EditAddressButtons = {
  edit: 'edit',
  add: 'add',
  addShipping: 'addShipping',
} as const

export type AddOrEditAddressButtonType = keyof typeof EditAddressButtons

interface EditAddressButtonProps {
  type: AddOrEditAddressButtonType
  isModalOpeningOnError?: boolean
  hasError?: boolean
  text?: ReactNode
  variant?: ButtonVariant
  onClick?: () => void
  onClose?: () => void
  onSave?: (fulfilmentDetails: FulfilmentDetail[]) => void
  fulfilmentDetail?: FulfilmentDetail
  lineItemLocalId?: string
  onOpenModal: () => void
}

export const AddOrEditAddressButton: React.FC<EditAddressButtonProps> = ({
  type,
  isModalOpeningOnError,
  hasError,
  text,
  variant,
  onClick,
  onClose,
  onSave,
  fulfilmentDetail,
  onOpenModal,
  lineItemLocalId,
}: EditAddressButtonProps) => {
  const { isDisabled } = useSaleFulfilment(lineItemLocalId || '')
  const isAddType =
    type === EditAddressButtons.add || type === EditAddressButtons.addShipping
  const icon = type === EditAddressButtons.edit ? 'pencil' : 'plus'

  const isFeatureEnabled = getFeatureFromCache(
    FEATURE_SALE_ADDRESS_MODAL_REFACTOR
  )

  // Legacy mode state and handlers (only used when FF is off)
  const [showSaleAddressModal, setShowSaleAddressModal] = useState(false)
  const dispatch = useAppDispatch()
  const saleManager = useSaleManager()
  const savedFulfilmentDetails = useAppSelector(selectSaleFulfilmentDetail)
  const fulfilmentDetails = fulfilmentDetail ?? savedFulfilmentDetails

  // Close handler for legacy inline modal
  const handleModalClose = useCallback(
    (updatedFulfillmentDetails?: FulfilmentDetail) => {
      if (updatedFulfillmentDetails && !onSave) {
        dispatch(
          setFulfilmentDetails([
            { ...updatedFulfillmentDetails, shouldAddNewAddress: false },
          ])
        )
      }
      setShowSaleAddressModal(false)
      onClose?.()
    },
    [dispatch, onClose, onSave]
  )

  // Save handler for legacy inline modal
  const handleModalSave = useCallback(
    (fulfilmentDetails: FulfilmentDetail[]) => {
      if (onSave) {
        onSave(fulfilmentDetails)
        return
      }
      dispatch(setFulfilmentDetails(fulfilmentDetails))
      saleManager.updateFulfillmentDetails(fulfilmentDetails)
    },
    [dispatch, onSave, saleManager]
  )

  const classNameMH = isModalOpeningOnError
    ? isAddType
      ? 'vd-ml1'
      : 'vd-mr1'
    : ''
  const classNameMV = !isAddType && !text ? 'vd-mt5' : ''

  useEffect(() => {
    if (hasError && isModalOpeningOnError) {
      if (isFeatureEnabled) {
        onOpenModal()
      } else {
        setShowSaleAddressModal(true)
      }
    }
  }, [hasError, isModalOpeningOnError, onOpenModal, isFeatureEnabled])

  return (
    <>
      {!isDisabled && (
        <Button
          disabled={isDisabled}
          modifier={text ? undefined : 'text'}
          variant={variant || 'supplementary'}
          onClick={() => {
            onClick?.()
            if (isFeatureEnabled) {
              onOpenModal()
            } else {
              setShowSaleAddressModal(true)
            }
          }}
          className={`${classNameMH} ${classNameMV}`}
          data-track={
            type === EditAddressButtons.edit
              ? HEAP_DATA_TRACK_EDIT_ADDRESS
              : HEAP_DATA_TRACK_ADD_ADDRESS
          }
        >
          {text || (
            <>
              <Icon icon={icon} className={isAddType ? 'vd-mr1' : ''} />
              {isAddType && <T _str="Add address" _context="Action link" />}
            </>
          )}
        </Button>
      )}

      {!isFeatureEnabled && showSaleAddressModal && (
        <SaleAddressModal
          formType={type}
          fulfillmentDetails={fulfilmentDetails}
          onClose={handleModalClose}
          onSave={handleModalSave}
        />
      )}
    </>
  )
}
